import { useState } from "react";
import Navbar from "../components/Navbar";
import SiteFooter from "../components/Footer";
import { ArrowRight, Loader2, CheckCircle, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "../translations";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const companySizes = ["1 - 200", "200 - 500", "500 - 1000", "1000 - 5000", "+5000"];
const managerCounts = ["5 - 10", "10 - 20", "20 - 50", "+50"];
const m365Tools = ["Microsoft Teams", "Outlook", "Microsoft Copilot"];

const PilotPage = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    company: "",
    company_size: "",
    manager_count: "",
    tools: [],
    noTools: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleToolToggle = (tool) => {
    if (tool === "none") {
      setFormData((prev) => ({
        ...prev,
        noTools: !prev.noTools,
        tools: !prev.noTools ? [] : prev.tools,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        noTools: false,
        tools: prev.tools.includes(tool)
          ? prev.tools.filter((t) => t !== tool)
          : [...prev.tools, tool],
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.email || !formData.company || !formData.company_size || !formData.manager_count) {
      toast.error(language === "fr" ? "Veuillez remplir tous les champs obligatoires" :
                  language === "es" ? "Complete todos los campos obligatorios" :
                  "Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/pilot-application`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: formData.full_name,
          email: formData.email,
          company: formData.company,
          company_size: formData.company_size,
          manager_count: formData.manager_count,
          tools: formData.noTools ? ["Aucun"] : formData.tools,
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        throw new Error("Submission failed");
      }
    } catch {
      toast.error(language === "fr" ? "Erreur lors de l'envoi. Réessayez." :
                  language === "es" ? "Error al enviar. Inténtelo de nuevo." :
                  "Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const pilotT = language === "fr" ? {
    title: "Rejoindre le pilote Cayus",
    subtitle: "Nous sélectionnons 5 organisations pour co-construire l'avenir de la mesure du leadership.\nDites-nous en un peu plus.",
    bloc1: "Vous",
    fullName: "Prénom / Nom",
    email: "Email professionnel",
    bloc2: "Votre organisation",
    company: "Nom de l'entreprise",
    companySize: "Taille de l'entreprise",
    managerCount: "Nombre de managers concernés",
    bloc3: "Votre environnement Microsoft 365",
    bloc3Sub: "Quels outils utilisez-vous déjà ?",
    noTools: "Aucun des trois pour l'instant",
    submit: "Envoyer ma candidature",
    submitSub: "Notre équipe vous répond sous 48h pour confirmer votre éligibilité.",
    successTitle: "Candidature reçue.",
    successMsg: "Nous analysons votre profil et revenons vers vous sous 48h. En attendant, si vous avez une question urgente :",
    successEmail: "renaud@cayus.ai",
    back: "Retour au site",
  } : language === "es" ? {
    title: "Unirse al piloto Cayus",
    subtitle: "Seleccionamos 5 organizaciones para co-construir el futuro de la medición del liderazgo.\nCuéntenos un poco más.",
    bloc1: "Usted",
    fullName: "Nombre / Apellido",
    email: "Email profesional",
    bloc2: "Su organización",
    company: "Nombre de la empresa",
    companySize: "Tamaño de la empresa",
    managerCount: "Número de managers afectados",
    bloc3: "Su entorno Microsoft 365",
    bloc3Sub: "¿Qué herramientas usa actualmente?",
    noTools: "Ninguna de las tres por ahora",
    submit: "Enviar mi candidatura",
    submitSub: "Nuestro equipo le responde en 48h para confirmar su elegibilidad.",
    successTitle: "Candidatura recibida.",
    successMsg: "Analizamos su perfil y le contactamos en 48h. Si tiene una pregunta urgente:",
    successEmail: "renaud@cayus.ai",
    back: "Volver al sitio",
  } : {
    title: "Join the Cayus Pilot",
    subtitle: "We're selecting 5 organisations to co-build the future of leadership measurement.\nTell us a bit more.",
    bloc1: "About You",
    fullName: "First / Last Name",
    email: "Work Email",
    bloc2: "Your Organisation",
    company: "Company Name",
    companySize: "Company Size",
    managerCount: "Number of managers involved",
    bloc3: "Your Microsoft 365 Environment",
    bloc3Sub: "Which tools do you already use?",
    noTools: "None of the three yet",
    submit: "Submit my application",
    submitSub: "Our team will respond within 48h to confirm your eligibility.",
    successTitle: "Application received.",
    successMsg: "We're reviewing your profile and will get back to you within 48h. In the meantime, if you have an urgent question:",
    successEmail: "renaud@cayus.ai",
    back: "Back to site",
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="pilot-page">
      <Navbar isScrolled={true} />
      <div className="flex flex-col items-center pt-28 pb-16 px-4">
      <div className="w-full max-w-lg">
        {/* Back link */}
        <a
          href="/"
          className="inline-flex items-center gap-2 text-sm text-[#5E6C83] hover:text-[#1B1B1B] mb-8 transition-colors"
          data-testid="pilot-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          {pilotT.back}
        </a>

        {/* Header */}
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1B1B1B] mb-3" data-testid="pilot-page-title">
          {pilotT.title}
        </h1>
        <p className="text-base text-[#4D4D4D] mb-10 whitespace-pre-line leading-relaxed">
          {pilotT.subtitle}
        </p>

        {isSubmitted ? (
          <div className="bg-white border border-emerald-200 rounded-2xl p-8 text-center" data-testid="pilot-success">
            <CheckCircle className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#1B1B1B] mb-2">{pilotT.successTitle}</h2>
            <p className="text-[#4D4D4D] mb-2">{pilotT.successMsg}</p>
            <a href="mailto:renaud@cayus.ai" className="text-[#0047AB] font-medium hover:underline">{pilotT.successEmail}</a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Bloc 1 - Vous */}
            <div className="bg-white border border-[#D9D9D9] rounded-2xl p-6">
              <h3 className="font-semibold text-[#1B1B1B] mb-4">{pilotT.bloc1}</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-[#3A3F4B] mb-2 block">{pilotT.fullName} *</Label>
                  <Input
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    className="border-[#D9D9D9]"
                    required
                    data-testid="pilot-input-name"
                  />
                </div>
                <div>
                  <Label className="text-[#3A3F4B] mb-2 block">{pilotT.email} *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="border-[#D9D9D9]"
                    placeholder="prenom@entreprise.com"
                    required
                    data-testid="pilot-input-email"
                  />
                </div>
              </div>
            </div>

            {/* Bloc 2 - Organisation */}
            <div className="bg-white border border-[#D9D9D9] rounded-2xl p-6">
              <h3 className="font-semibold text-[#1B1B1B] mb-4">{pilotT.bloc2}</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-[#3A3F4B] mb-2 block">{pilotT.company} *</Label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="border-[#D9D9D9]"
                    required
                    data-testid="pilot-input-company"
                  />
                </div>
                <div>
                  <Label className="text-[#3A3F4B] mb-2 block">{pilotT.companySize} *</Label>
                  <Select onValueChange={(v) => setFormData((p) => ({ ...p, company_size: v }))} value={formData.company_size}>
                    <SelectTrigger className="border-[#D9D9D9]" data-testid="pilot-select-size">
                      <SelectValue placeholder={pilotT.companySize} />
                    </SelectTrigger>
                    <SelectContent>
                      {companySizes.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#3A3F4B] mb-2 block">{pilotT.managerCount} *</Label>
                  <Select onValueChange={(v) => setFormData((p) => ({ ...p, manager_count: v }))} value={formData.manager_count}>
                    <SelectTrigger className="border-[#D9D9D9]" data-testid="pilot-select-managers">
                      <SelectValue placeholder={pilotT.managerCount} />
                    </SelectTrigger>
                    <SelectContent>
                      {managerCounts.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Bloc 3 - M365 */}
            <div className="bg-white border border-[#D9D9D9] rounded-2xl p-6">
              <h3 className="font-semibold text-[#1B1B1B] mb-1">{pilotT.bloc3}</h3>
              <p className="text-sm text-[#4D4D4D] mb-4">{pilotT.bloc3Sub}</p>
              <div className="flex flex-col gap-3">
                {m365Tools.map((tool) => (
                  <label key={tool} className="flex items-center gap-3 cursor-pointer" data-testid={`pilot-tool-${tool.toLowerCase().replace(/\s/g, '-')}`}>
                    <Checkbox
                      checked={formData.tools.includes(tool)}
                      onCheckedChange={() => handleToolToggle(tool)}
                      disabled={formData.noTools}
                      aria-label={tool}
                      data-testid={`pilot-checkbox-${tool.toLowerCase().replace(/\s/g, "-")}`}
                    />
                    <span className="text-sm text-[#3A3F4B]">{tool}</span>
                  </label>
                ))}
                <div className="border-t border-[#ECECEC] pt-3 mt-3">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="pilot-tool-none">
                    <Checkbox
                      checked={formData.noTools}
                      onCheckedChange={() => handleToolToggle("none")}
                      aria-label={pilotT.noTools}
                      data-testid="pilot-checkbox-none"
                    />
                    <span className="text-sm text-[#3A3F4B]">{pilotT.noTools}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-full px-8 py-4 bg-[#0047AB] text-white font-semibold hover:bg-[#0B3F8F] transition-all text-base"
                data-testid="pilot-submit-button"
              >
                {isSubmitting ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" />{pilotT.submit}</>
                ) : (
                  <>{pilotT.submit}<ArrowRight className="ml-2 h-5 w-5" /></>
                )}
              </Button>
              <p className="text-sm text-[#5E6C83] text-center mt-3">{pilotT.submitSub}</p>
            </div>
          </form>
        )}
      </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default PilotPage;
